/**
 * ScratchGame — Scratch Card screen (game_type: "scratch").
 *
 * Canvas-based scratch mechanic distinct from both `Game.jsx` (bet-and-reveal)
 * and `CrashGame.jsx` (climbing multiplier). The player:
 *   1. picks a card tier (from GET /tiers?currency=<active>)
 *   2. taps BUY — POST /buy debits balance, returns model + hidden prize
 *   3. drags on the canvas to erase a foil overlay (mouse + touch)
 *   4. at 60% erased, we auto-fire POST /scratch → banner reveals prize + payout
 *
 * State machine
 * -------------
 *   idle          → tier picker + Buy button active.
 *   buying        → POST /buy in flight. Button spinner.
 *   unscratched   → card in hand. Canvas foil covers the dressed image.
 *   scratching    → user is dragging; % erased is tracked.
 *   revealing     → 60% threshold hit → POST /scratch → response arriving.
 *   resolved      → banner shown for 4 s → back to idle (round + result cleared).
 *
 * Canvas mechanic
 * ---------------
 *   - <canvas> layered over the dressed <img>. Pointer events on the canvas
 *     wrapper (mouse + touch), using `globalCompositeOperation="destination-out"`
 *     with a soft circular brush (radius 26 px) at each move.
 *   - Every 200 ms we sample the alpha channel to compute the % erased.
 *   - At ≥ 60 %, phase→revealing, we clear the remaining foil in one motion,
 *     swap the underlying <img> src to `bikini_url`, and fire /scratch.
 *
 * Provably-fair drawer
 *   Committed hash pre-scratch; revealed seed post-resolve; deep-link to /verify
 *   with the seeds prefilled.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ChevronDown, History as HistoryIcon, Lock, LogOut,
  Sparkles, Ticket, ShieldCheck, X,
} from "lucide-react";
import { Toaster } from "sonner";

import api, { asset } from "../../lib/api";
import { extractError } from "../../lib/formatError";
import { useAuth } from "../../context/AuthContext";
import { useCurrency } from "../../context/CurrencyContext";
import {
  CURRENCIES,
  formatAmount,
  getBalance,
} from "../../lib/currency";
import ModeSegmented, { LegacyModeAliases } from "../../components/ui-r1/ModeSegmented";
import { Badge } from "../../components/ui-r1/Badge";
import CardOfTheDayBanner from "../../components/CardOfTheDayBanner";

const SLUG = "scratch-card";
const SCRATCH_THRESHOLD = 0.60;       // fraction of foil erased that triggers auto-reveal
const SAMPLE_INTERVAL_MS = 200;       // how often we sample the canvas
const BRUSH_RADIUS = 26;              // px, at canvas-native resolution
const RESULT_HOLD_MS = 4500;
// Prize multipliers shown on the info card (probabilities are intentionally
// omitted — the backend surfaces the full weighted table; here we just tease).
const PRIZE_TIERS = ["200×", "50×", "10×", "3×", "1.5×"];

export default function ScratchGame() {
  const nav = useNavigate();
  const { user, refresh, logout } = useAuth();
  const { activeCurrency, setActiveCurrency } = useCurrency();

  const [gameMeta, setGameMeta] = useState(null);
  const [tiers, setTiers] = useState([]);           // [{price, label}, ...]
  const [selectedTier, setSelectedTier] = useState(null);
  const [mode, setMode] = useState("demo");
  const [phase, setPhase] = useState("idle");        // idle | buying | unscratched | scratching | revealing | resolved
  const [round, setRound] = useState(null);          // /buy response
  const [result, setResult] = useState(null);        // /scratch response
  const [scratchPct, setScratchPct] = useState(0);
  const [error, setError] = useState(null);
  const [showFair, setShowFair] = useState(false);
  const [showCurrency, setShowCurrency] = useState(false);

  const balance = getBalance(user, mode, activeCurrency);
  const realBalance = getBalance(user, "real", activeCurrency);
  const demoBalance = getBalance(user, "demo", activeCurrency);
  const currencyForRound = round?.currency || activeCurrency;

  // -------- meta + tiers on mount / currency change --------
  useEffect(() => {
    api.get(`/games/${SLUG}`).then(({ data }) => setGameMeta(data)).catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    api.get(`/games/${SLUG}/tiers`, { params: { currency: activeCurrency } })
      .then(({ data }) => {
        if (!alive) return;
        const t = data.tiers || [];
        setTiers(t);
        // Snap selection to the middle tier on currency flip.
        setSelectedTier((prev) => {
          if (prev && t.some((x) => x.price === prev.price)) return prev;
          return t[Math.floor(t.length / 2)] || t[0] || null;
        });
      })
      .catch(() => setTiers([]));
    return () => { alive = false; };
  }, [activeCurrency]);

  // -------- actions --------
  const buyCard = useCallback(async () => {
    if (!selectedTier) return;
    setError(null);
    setResult(null);
    setScratchPct(0);
    setPhase("buying");
    try {
      const { data } = await api.post(`/games/${SLUG}/buy`, {
        currency: activeCurrency,
        mode,
        card_price: selectedTier.price,
      });
      setRound(data);
      // Preload the bikini frame so the reveal has no network flash.
      if (data.model_urls?.bikini) new Image().src = asset(data.model_urls.bikini);
      setPhase("unscratched");
      refresh();
    } catch (e) {
      setError(extractError(e));
      setPhase("idle");
    }
  }, [selectedTier, activeCurrency, mode, refresh]);

  const revealCard = useCallback(async () => {
    if (!round?.round_id) return;
    setPhase("revealing");
    try {
      const { data } = await api.post(`/games/${SLUG}/scratch`, { round_id: round.round_id });
      setResult(data);
      setPhase("resolved");
      refresh();
      // Auto-return to idle so the tier picker + Buy button come back.
      setTimeout(() => {
        setPhase("idle");
        setRound(null);
        setResult(null);
        setScratchPct(0);
      }, RESULT_HOLD_MS);
    } catch (e) {
      setError(extractError(e));
      // Roll back to unscratched so the player can try again.
      setPhase("unscratched");
    }
  }, [round, refresh]);

  const playAgain = useCallback(() => {
    setPhase("idle");
    setRound(null);
    setResult(null);
    setScratchPct(0);
    setError(null);
  }, []);

  // -------- ui derived --------
  const dressedUrl = round?.model_urls?.dressed ? asset(round.model_urls.dressed) : null;
  const bikiniUrl  = round?.model_urls?.bikini  ? asset(round.model_urls.bikini)  : null;
  // Show the bikini image the moment we auto-fire /scratch so the reveal
  // animation is snappy — otherwise the swap has to wait for the network.
  const displayImage = (phase === "revealing" || phase === "resolved") ? bikiniUrl : dressedUrl;

  const canBuy =
    phase === "idle" &&
    !!selectedTier &&
    balance >= selectedTier.price;

  const currencySymbol = useMemo(() => {
    const c = CURRENCIES.find((x) => x.code === activeCurrency);
    return c?.symbol || "";
  }, [activeCurrency]);

  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden bg-base text-ink" data-testid="scratch-game-page">
      <Toaster theme="dark" position="top-center" />

      {/* ---- Top strip ---- */}
      <div className="h-11 shrink-0 flex items-center gap-2 px-2 sm:px-3 border-b border-line bg-elevated/70 backdrop-blur relative z-30">
        <button
          onClick={() => nav("/dashboard")}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-ink-2 hover:text-ink hover:bg-highest transition"
          aria-label="Back to dashboard"
          data-testid="game-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <Badge variant="hot" className="hidden sm:inline-flex" data-testid="game-title-badge">
          {gameMeta?.name || "Scratch Card"}
        </Badge>

        <div className="flex items-center gap-1.5" data-testid="game-mode-cluster">
          <ModeSegmented
            mode={mode}
            setMode={(m) => phase === "idle" && setMode(m)}
            realBalance={realBalance}
            demoBalance={demoBalance}
            disabled={phase !== "idle"}
            size="compact"
          />
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowCurrency((s) => !s)}
              disabled={phase !== "idle"}
              className={
                "h-9 px-2.5 rounded-lg border border-line hover:border-pink text-[10px] tracking-widest uppercase flex items-center gap-1.5 shrink-0 " +
                "text-ink hover:bg-highest transition font-mono " +
                (phase !== "idle" ? "opacity-60 cursor-not-allowed" : "cursor-pointer")
              }
              aria-label="Change currency"
              data-testid="game-currency-btn"
            >
              <span className="text-gold">{activeCurrency}</span>
              <ChevronDown className="w-3 h-3 text-ink-3" />
            </button>
            {showCurrency && phase === "idle" && (
              <>
                <button className="fixed inset-0 z-40" aria-label="close" onClick={() => setShowCurrency(false)} />
                <div className="absolute right-0 top-full mt-1.5 w-56 z-50 rounded-lg border border-line bg-elevated shadow-card-lg p-1.5">
                  <div className="px-2 py-1 text-[9px] tracking-[0.3em] uppercase text-ink-3">Buy in</div>
                  {CURRENCIES.map((c) => {
                    const bal = getBalance(user, mode, c.code);
                    const active = c.code === activeCurrency;
                    return (
                      <button
                        key={c.code}
                        type="button"
                        onClick={() => { setActiveCurrency(c.code); setShowCurrency(false); }}
                        className={
                          "w-full flex items-center justify-between px-2 py-1.5 rounded-md text-xs transition " +
                          (active ? "bg-pink/10 text-pink" : "text-ink-2 hover:text-ink hover:bg-highest")
                        }
                        data-testid={`game-currency-option-${c.code}`}
                      >
                        <span className="flex items-center gap-2">
                          <span className="font-mono text-ink-3 w-4 inline-flex justify-center">{c.symbol}</span>
                          <span className="uppercase tracking-widest text-[10px]">{c.code}</span>
                        </span>
                        <span className="font-mono text-[10px] text-ink-3">{formatAmount(bal, c.code)}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
        <LegacyModeAliases />
        <span className="hidden font-mono text-gold" data-testid="game-balance">{formatAmount(balance ?? 0, activeCurrency)}</span>

        <div className="flex-1" />
        <Link to="/history" className="w-8 h-8 flex items-center justify-center rounded-lg text-ink-2 hover:text-pink hover:bg-highest transition" aria-label="History" data-testid="nav-history">
          <HistoryIcon className="w-4 h-4" />
        </Link>
        <Link to="/verify" className="w-8 h-8 flex items-center justify-center rounded-lg text-ink-2 hover:text-gold hover:bg-highest transition" aria-label="Verifier" data-testid="nav-verify">
          <ShieldCheck className="w-4 h-4" />
        </Link>
        <button
          onClick={async () => { await logout(); nav("/"); }}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-ink-2 hover:text-danger hover:bg-highest transition"
          aria-label="Log out"
          data-testid="game-logout-btn"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </div>

      {/* ---- Main ---- */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden overflow-y-auto">
        <div className="flex flex-col items-center justify-start gap-4 p-4 sm:p-6 max-w-xl w-full mx-auto">

          {/* ---- Card frame ---- */}
          <div
            className="relative w-full max-w-[360px] aspect-[3/4] rounded-2xl overflow-hidden border-2 border-gold/40 shadow-glow-gold bg-base"
            data-testid="scratch-card-frame"
          >
            {displayImage ? (
              <img
                src={displayImage}
                alt={round?.model_id || "card"}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="scratch-card-image"
                data-stage={phase === "revealing" || phase === "resolved" ? "bikini" : "dressed"}
                draggable="false"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-ink-3 text-[10px] tracking-[0.3em] uppercase animate-pulse">
                {phase === "idle" ? "Pick a tier · buy · scratch" : "Loading…"}
              </div>
            )}

            {/* Foil canvas — only mounted while there's a card to scratch */}
            {(phase === "unscratched" || phase === "scratching" || phase === "revealing") && (
              <ScratchCanvas
                round={round}
                onProgress={setScratchPct}
                onScratchStart={() => phase === "unscratched" && setPhase("scratching")}
                onThreshold={revealCard}
                threshold={SCRATCH_THRESHOLD}
                frozen={phase === "revealing"}
              />
            )}

            {/* Scratch-me hint (fades once user starts) */}
            {phase === "unscratched" && (
              <div
                className="absolute inset-x-0 top-3 text-center text-[10px] tracking-[0.35em] uppercase text-gold pointer-events-none z-30 drop-shadow"
                data-testid="scratch-hint"
              >
                Scratch to reveal
              </div>
            )}

            {/* Progress ring — bottom-right, only while scratching */}
            {(phase === "scratching" || phase === "revealing") && (
              <div
                className="absolute bottom-2 right-2 px-2 py-1 rounded-full bg-base/80 border border-gold/50 text-[10px] font-mono text-gold pointer-events-none z-30"
                data-testid="scratch-progress"
              >
                {Math.floor(scratchPct * 100)}%
              </div>
            )}

            {/* Result banner overlay (post-scratch) */}
            <AnimatePresence>
              {phase === "resolved" && result && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className={
                    "absolute inset-0 flex items-center justify-center pointer-events-none z-40 " +
                    (result.won ? "bg-gradient-to-b from-transparent via-base/40 to-base/70" : "bg-gradient-to-b from-transparent via-base/50 to-base/80")
                  }
                  data-testid={result.won ? "scratch-won-banner" : "scratch-lost-banner"}
                >
                  <div
                    className={
                      "px-6 py-4 rounded-2xl border-2 backdrop-blur-md text-center " +
                      (result.won
                        ? "bg-gold/15 border-gold shadow-glow-gold"
                        : "bg-danger/15 border-danger animate-shake")
                    }
                  >
                    <div className={
                      "text-[10px] tracking-[0.4em] uppercase mb-1 flex items-center justify-center gap-1.5 " +
                      (result.won ? "text-gold" : "text-danger")
                    }>
                      {result.won
                        ? <><Sparkles className="w-3 h-3" /> Winner</>
                        : "Better luck"}
                    </div>
                    {result.won ? (
                      <>
                        <div className="font-mono text-4xl font-bold gold-text leading-none" data-testid="scratch-won-payout">
                          +{formatAmount(result.payout, result.currency)}
                        </div>
                        <div className="text-[11px] tracking-[0.3em] uppercase text-ink-2 mt-1.5" data-testid="scratch-won-multiplier">
                          {Number(result.prize_multiplier).toString()}× multiplier
                        </div>
                      </>
                    ) : (
                      <div className="text-[11px] tracking-[0.3em] uppercase text-ink-2" data-testid="scratch-lost-line">
                        −{formatAmount(result.card_price, result.currency)}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ---- Tier picker (idle only) ---- */}
          {phase === "idle" && tiers.length > 0 && (
            <div className="w-full max-w-[360px]" data-testid="scratch-tier-picker">
              <div className="text-[10px] tracking-[0.35em] uppercase text-ink-3 mb-1.5 text-center">
                Card tier
              </div>
              <div className="grid grid-cols-3 gap-2">
                {tiers.map((t) => {
                  const active = selectedTier?.price === t.price;
                  return (
                    <button
                      key={t.price}
                      type="button"
                      onClick={() => setSelectedTier(t)}
                      className={
                        "h-14 rounded-xl border-2 flex items-center justify-center font-mono text-base transition " +
                        (active
                          ? "border-gold bg-gold/15 text-gold shadow-glow-gold scale-[1.02]"
                          : "border-line bg-elevated text-ink-2 hover:border-gold/60 hover:text-ink")
                      }
                      data-testid={`scratch-tier-${t.price}`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ---- Contextual CTA ---- */}
          <div className="w-full max-w-[360px] flex flex-col gap-2">
            {error && (
              <div className="text-[11px] text-danger border border-danger/60 bg-danger/10 rounded-lg px-3 py-1.5" data-testid="game-error">
                {error}
              </div>
            )}

            {phase === "idle" && (
              <button
                onClick={buyCard}
                disabled={!canBuy}
                className={
                  "h-12 rounded-lg font-semibold tracking-[0.12em] uppercase text-white transition " +
                  "bg-brand-gradient shadow-glow-pink " +
                  "hover:brightness-110 hover:scale-[1.01] active:scale-[0.99] " +
                  "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:brightness-100 disabled:hover:scale-100 " +
                  "flex items-center justify-center gap-2"
                }
                data-testid="buy-card-btn"
              >
                <Ticket className="w-4 h-4" />
                <span>
                  Buy Card · {selectedTier ? `${currencySymbol}${selectedTier.price % 1 === 0 ? selectedTier.price : selectedTier.price.toFixed(2)}` : "…"}
                </span>
              </button>
            )}

            {phase === "buying" && (
              <button disabled className="h-12 rounded-lg bg-elevated border border-line text-ink-3 flex items-center justify-center gap-2 opacity-70" data-testid="buy-card-btn">
                <div className="w-3 h-3 rounded-full border-2 border-pink border-t-transparent animate-spin" />
                <span className="text-xs tracking-[0.3em] uppercase">Buying…</span>
              </button>
            )}

            {(phase === "unscratched" || phase === "scratching") && (
              <div className="h-12 rounded-lg border border-gold/40 bg-elevated/40 flex items-center justify-center gap-2" data-testid="scratch-cta-hint">
                <span className="text-[11px] tracking-[0.3em] uppercase text-gold">
                  Scratch the card above
                </span>
              </div>
            )}

            {phase === "revealing" && (
              <div className="h-12 rounded-lg border border-gold/60 bg-gold/10 flex items-center justify-center gap-2" data-testid="scratch-cta-revealing">
                <div className="w-3 h-3 rounded-full border-2 border-gold border-t-transparent animate-spin" />
                <span className="text-[11px] tracking-[0.3em] uppercase text-gold">Revealing…</span>
              </div>
            )}

            {phase === "resolved" && (
              <button
                onClick={playAgain}
                className="h-12 rounded-lg bg-brand-gradient text-white font-semibold tracking-[0.12em] uppercase shadow-glow-pink hover:brightness-110 transition flex items-center justify-center gap-2"
                data-testid="scratch-play-again-btn"
              >
                <Ticket className="w-4 h-4" /> Play again
              </button>
            )}
          </div>

          {/* ---- Prize table info ---- */}
          <div className="w-full max-w-[360px] rounded-lg border border-line/60 bg-elevated/30 px-3 py-2 flex items-center justify-between" data-testid="scratch-prize-table">
            <span className="text-[10px] tracking-[0.3em] uppercase text-ink-3">Possible</span>
            <div className="flex items-center gap-1.5">
              {PRIZE_TIERS.map((t, i) => (
                <span
                  key={t}
                  className={
                    "font-mono text-[10px] px-1.5 py-0.5 rounded " +
                    (i === 0 ? "bg-gold/20 text-gold" : "bg-highest/50 text-ink-2")
                  }
                >
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* ---- Card of the Day banner ---- */}
          {/* Renders 200×/day cap state — remaining count + countdown
              to UTC midnight, red rail when exhausted, hidden when
              admin has disabled the mechanic entirely. Sits directly
              under the prize teaser so the daily-scarcity narrative
              is visible without pushing the buy button below the
              fold. */}
          <CardOfTheDayBanner />

          {/* ---- PF pill ---- */}
          <button
            onClick={() => setShowFair(true)}
            disabled={!round}
            className="h-7 flex items-center justify-center gap-2 rounded-full border border-line hover:border-gold text-[10px] tracking-[0.3em] uppercase text-ink-3 hover:text-gold transition disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="fair-toggle-btn"
          >
            <Lock className="w-3 h-3" />
            <span>Provably fair {round ? `· nonce ${round.nonce ?? 0}` : ""}</span>
          </button>

        </div>
      </div>

      {showFair && round && (
        <ScratchFairDrawer round={round} result={result} onClose={() => setShowFair(false)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Canvas foil layer — mouse + touch, alpha-sampled progress, self-clearing.
// ---------------------------------------------------------------------------
function ScratchCanvas({ round, onProgress, onScratchStart, onThreshold, threshold, frozen }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const dragRef = useRef(false);
  const thresholdFiredRef = useRef(false);
  const lastPointRef = useRef(null);

  // Draw the foil once per round (or when the round_id changes).
  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    thresholdFiredRef.current = false;

    // Match backing store to CSS size × devicePixelRatio for sharp brushing.
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const rect = wrap.getBoundingClientRect();
    canvas.width = Math.floor(rect.width * dpr);
    canvas.height = Math.floor(rect.height * dpr);
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    // Foil gradient — gold → pink diagonal, with a subtle sparkle grain.
    const g = ctx.createLinearGradient(0, 0, rect.width, rect.height);
    g.addColorStop(0, "#d4a13a");     // gold
    g.addColorStop(0.5, "#c73f6b");   // rose
    g.addColorStop(1, "#8b3fb1");     // violet
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, rect.width, rect.height);

    // Sparkle grain: 200 tiny white specks so it looks like foil, not paint.
    ctx.save();
    ctx.globalAlpha = 0.18;
    for (let i = 0; i < 240; i++) {
      const x = Math.random() * rect.width;
      const y = Math.random() * rect.height;
      const r = Math.random() * 1.6 + 0.4;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = i % 3 === 0 ? "#ffffff" : "#ffd97d";
      ctx.fill();
    }
    ctx.restore();

    // Big "SCRATCH ME" label so the foil is unambiguous. Center-align.
    ctx.save();
    ctx.globalAlpha = 0.85;
    ctx.font = "600 24px 'Space Grotesk', system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#1a1512";
    ctx.fillText("SCRATCH", rect.width / 2, rect.height / 2 - 12);
    ctx.fillText("ME", rect.width / 2, rect.height / 2 + 20);
    ctx.restore();
  }, [round?.round_id]);

  // Sample every SAMPLE_INTERVAL_MS to compute % erased.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const interval = setInterval(() => {
      const ctx = canvas.getContext("2d");
      const w = canvas.width;
      const h = canvas.height;
      // For perf: sample a 40×60 grid of pixels rather than every pixel.
      const stepX = Math.max(1, Math.floor(w / 40));
      const stepY = Math.max(1, Math.floor(h / 60));
      let sampled = 0;
      let cleared = 0;
      const imgData = ctx.getImageData(0, 0, w, h).data;
      for (let y = 0; y < h; y += stepY) {
        for (let x = 0; x < w; x += stepX) {
          const alpha = imgData[(y * w + x) * 4 + 3];
          sampled++;
          if (alpha < 24) cleared++;
        }
      }
      const pct = sampled ? cleared / sampled : 0;
      onProgress(pct);
      if (pct >= threshold && !thresholdFiredRef.current) {
        thresholdFiredRef.current = true;
        // Clear the remaining foil for a satisfying finish.
        ctx.save();
        ctx.globalCompositeOperation = "destination-out";
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, w, h);
        ctx.restore();
        onThreshold();
      }
    }, SAMPLE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [onProgress, onThreshold, threshold, round?.round_id]);

  // ---------- pointer handling ----------
  const localXY = (evt) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const src = "touches" in evt && evt.touches.length ? evt.touches[0]
              : "changedTouches" in evt && evt.changedTouches.length ? evt.changedTouches[0]
              : evt;
    return {
      x: src.clientX - rect.left,
      y: src.clientY - rect.top,
    };
  };

  const drawAt = (x, y) => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    ctx.save();
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(x, y, BRUSH_RADIUS, 0, Math.PI * 2);
    ctx.fill();
    // If we have a previous point, draw a thick line so fast swipes still connect.
    const last = lastPointRef.current;
    if (last) {
      ctx.lineWidth = BRUSH_RADIUS * 2;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(x, y);
      ctx.stroke();
    }
    ctx.restore();
    lastPointRef.current = { x, y };
  };

  const start = (e) => {
    if (frozen) return;
    dragRef.current = true;
    onScratchStart?.();
    const { x, y } = localXY(e);
    lastPointRef.current = null;
    drawAt(x, y);
    e.preventDefault?.();
  };
  const move = (e) => {
    if (!dragRef.current || frozen) return;
    const { x, y } = localXY(e);
    drawAt(x, y);
    e.preventDefault?.();
  };
  const end = () => {
    dragRef.current = false;
    lastPointRef.current = null;
  };

  return (
    <div
      ref={wrapRef}
      className="absolute inset-0 z-20 touch-none cursor-crosshair"
      onMouseDown={start}
      onMouseMove={move}
      onMouseUp={end}
      onMouseLeave={end}
      onTouchStart={start}
      onTouchMove={move}
      onTouchEnd={end}
      onTouchCancel={end}
      data-testid="scratch-canvas-wrap"
    >
      <canvas
        ref={canvasRef}
        className="w-full h-full block select-none"
        data-testid="scratch-canvas"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Provably-fair drawer — same shape as Game.jsx / CrashGame.jsx.
// ---------------------------------------------------------------------------
function ScratchFairDrawer({ round, result, onClose }) {
  const hash = round?.server_seed_committed_hash || "—";
  const clientSeed = round?.client_seed || "—";
  const nonce = String(round?.nonce ?? 0);
  const revealed = result?.server_seed || null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="fair-panel"
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 30, opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border border-line bg-elevated p-5 max-h-[80vh] overflow-y-auto"
        data-testid="fair-details"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-gold" />
            <div className="font-display italic text-xl text-ink">Provably fair</div>
          </div>
          <button onClick={onClose} className="text-ink-3 hover:text-ink" aria-label="Close"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-2.5 text-xs">
          <FairRow label="Server seed hash" value={hash} testid="fair-server-hash" />
          <FairRow label="Client seed" value={clientSeed} testid="fair-client-seed" />
          <FairRow label="Nonce" value={nonce} testid="fair-nonce" />
          {revealed && <FairRow label="Server seed (revealed)" value={revealed} testid="fair-server-seed-revealed" />}
        </div>
        <p className="text-[11px] text-ink-2 leading-relaxed mt-4">
          The prize was picked at buy time via HMAC(server_seed, &quot;{clientSeed}:{nonce}&quot;) → 32-bit → mod
          10&nbsp;000 walked through the weighted table. The hash committed to your card is
          the only thing you saw before scratching — verify the seed matches after.
        </p>
        <Link
          to={revealed
            ? `/verify?server_seed=${encodeURIComponent(revealed)}&client_seed=${encodeURIComponent(clientSeed)}&nonce=${nonce}&game_slug=${SLUG}`
            : "/verify"}
          onClick={onClose}
          className="mt-5 h-10 flex items-center justify-center rounded-lg bg-brand-gradient text-white text-[11px] tracking-[0.25em] uppercase shadow-glow-pink hover:brightness-110 transition"
          data-testid="fair-open-verify-link"
        >
          Verifier →
        </Link>
      </motion.div>
    </div>
  );
}

function FairRow({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const short = value.length > 26 ? `${value.slice(0, 12)}…${value.slice(-6)}` : value;
  return (
    <div className="flex justify-between items-center gap-2">
      <span className="text-[10px] tracking-[0.3em] uppercase text-ink-3">{label}</span>
      <button
        onClick={() => { navigator.clipboard?.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1000); }}
        title={value}
        className="text-ink hover:text-gold text-right whitespace-nowrap transition font-mono text-[11px]"
        data-testid={testid}
      >
        {copied ? "copied" : short}
      </button>
    </div>
  );
}
